import filterIcon from "../../assets/filter.png";
import {
  Badge,
  Button,
  Popover,
  Select,
  DatePicker,
  Radio,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { GET_MANAGERS_API_URL } from "../../config/constants";
import { apiFetch } from "../../utils/apiClient";
import { useBusinessDomains } from "../../hooks/useBusinessDomain";
import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment, TextField } from "@mui/material";


const EMPTY_MANAGER_OPTIONS = {
  "Client Partner": [],
  "Delivery Manager": [],
  "Project Manager": [],
};



export default function ProjectFilter({
  onFilterChange,
}) {
  const SEARCH_DEBOUNCE_MS = 400;
  const SEARCH_MAX_LENGTH = 100;
  const FILTER_POPUP_WIDTH = 400;

  const FILTER_DROPDOWN_CLASS = "project-filter-popup-dropdown";

  const FILTER_CONTROL_HEIGHT = 30;

  const FILTER_TEXT_SIZE = 11;

  const FILTER_LABEL_STYLE = {
    fontSize: FILTER_TEXT_SIZE,
    fontWeight: 600,
    color: "#475569",
    lineHeight: "16px",
  };

  const FILTER_CONTROL_STYLE = {
    width: "100%",
    height: FILTER_CONTROL_HEIGHT,
    fontSize: FILTER_TEXT_SIZE,
  };

  const FILTER_RADIO_STYLE = {
    marginInlineEnd: 0,
    fontSize: FILTER_TEXT_SIZE,
    lineHeight: "18px",
    whiteSpace: "nowrap",
  };

  const normalizedSessionRole = (
    sessionStorage.getItem("role") || ""
  )
    .trim()
    .toUpperCase();

  const isProjectManagerUser =
    normalizedSessionRole === "PROJECT MANAGER" ||
    normalizedSessionRole === "PM";

  const [filterData, setFilterData] = useState({
    search: "",
    statusFilter: undefined,
    deliveryManager: undefined,
    clientPartner: undefined,
    projectManager: undefined,
    dateRangeType: undefined,
    dateRange: undefined,
    contractCategory: undefined,
    businessArea: undefined,
    ragStatus: undefined,
  });

  /*
   * appliedFilterData = actually applied filters.
   * Badge count and dashboard filtering are based on this state.
   */
  const [appliedFilterData, setAppliedFilterData] =
    useState({
      search: "",
      statusFilter: undefined,
      deliveryManager: undefined,
      clientPartner: undefined,
      projectManager: undefined,
      dateRangeType: undefined,
      dateRange: undefined,
      contractCategory: undefined,
      businessArea: undefined,
      ragStatus: undefined,
    });

  const [selectedRoleType, setSelectedRoleType] = useState("");

  const [managerOptions, setManagerOptions] =
    useState(EMPTY_MANAGER_OPTIONS);

  const [managerOptionsLoading, setManagerOptionsLoading] =
    useState(false);

  const [filterOpen, setFilterOpen] = useState(false);

  const [dateFilterType, setDateFilterType] =
    useState();

  const lastAutoSearchValueRef = useRef(filterData.search);
  const appliedFilterDataRef = useRef(appliedFilterData);
  const searchTimeoutRef = useRef();

  const { businessDomainOptions, loading } =
    useBusinessDomains();

  useEffect(() => {
    appliedFilterDataRef.current = appliedFilterData;
  }, [appliedFilterData]);

  const availableRoleOptions = selectedRoleType
    ? managerOptions[selectedRoleType]
    : [];

  useEffect(() => {
    if (isProjectManagerUser) {
      setManagerOptions(EMPTY_MANAGER_OPTIONS);
      return;
    }

    const fetchManagers = async () => {
      setManagerOptionsLoading(true);

      try {
        const token =
          sessionStorage.getItem("token") ||
          sessionStorage.getItem("authToken") ||
          sessionStorage.getItem("accessToken");

        const tokenType =
          sessionStorage.getItem("tokenType") || "Bearer";

        const normalizedToken = token
          ?.replace(/^"|"$/g, "")
          .replace(/^Bearer\s+/i, "")
          .trim();

        const headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
        };

        if (normalizedToken) {
          headers.Authorization = `${tokenType} ${normalizedToken}`;
        }

        const response = await apiFetch(GET_MANAGERS_API_URL, {
          method: "GET",
          headers,
        }, "Failed to fetch managers");

        if (!response.ok) {
          throw new Error(
            `Failed to fetch managers: ${response.status}`,
          );
        }

        const data =
          (await response.json());

        setManagerOptions({
          "Client Partner": Array.isArray(data.cp)
            ? data.cp
            : [],
          "Delivery Manager": Array.isArray(data.dm)
            ? data.dm
            : [],
          "Project Manager": Array.isArray(data.pm)
            ? data.pm
            : [],
        });
      } catch (error) {
        console.error(
          "Unable to load manager options",
          error,
        );
        setManagerOptions(EMPTY_MANAGER_OPTIONS);
      } finally {
        setManagerOptionsLoading(false);
      }
    };

    void fetchManagers();
  }, [isProjectManagerUser]);

  useEffect(() => {
    const timeoutId = globalThis.setTimeout(() => {
      const trimmedSearch = filterData.search.trim();

      // Anything less than 3 characters means "no search" is applied.
      const effectiveSearch =
        trimmedSearch.length < 3 ? "" : trimmedSearch;

      if (
        lastAutoSearchValueRef.current === effectiveSearch
      ) {
        return;
      }

      lastAutoSearchValueRef.current = effectiveSearch;

      const updatedAppliedFilters = {
        ...appliedFilterDataRef.current,
        search: effectiveSearch,
      };

      setAppliedFilterData(updatedAppliedFilters);

      onFilterChange?.(updatedAppliedFilters);
    }, SEARCH_DEBOUNCE_MS);

    searchTimeoutRef.current = timeoutId;

    return () => globalThis.clearTimeout(timeoutId);
  }, [filterData.search, onFilterChange]);

  const availableRoles = (() => {
    switch (normalizedSessionRole) {
      case "CLIENT PARTNER":
        return ["Delivery Manager", "Project Manager"];

      case "DELIVERY MANAGER":
        return ["Client Partner", "Project Manager"];

      case "ADMIN":
      case "ACCOUNT MANAGER":
      case "PMO":
        return [
          "Client Partner",
          "Delivery Manager",
          "Project Manager",
        ];

      default:
        return [];
    }
  })();

  const handleRoleTypeChange = (
    roleType
  ) => {
    setSelectedRoleType(roleType);

    setFilterData((prev) => ({
      ...prev,
      clientPartner: undefined,
      deliveryManager: undefined,
      projectManager: undefined,
    }));
  };

  const handleRoleValueChange = (value) => {
    setFilterData((prev) => {
      const resetRoleFilters = {
        ...prev,
        clientPartner: undefined,
        deliveryManager: undefined,
        projectManager: undefined,
      };

      if (!value || !selectedRoleType) {
        return resetRoleFilters;
      }

      if (selectedRoleType === "Client Partner") {
        return {
          ...resetRoleFilters,
          clientPartner: value,
        };
      }

      if (selectedRoleType === "Delivery Manager") {
        return {
          ...resetRoleFilters,
          deliveryManager: value,
        };
      }

      return {
        ...resetRoleFilters,
        projectManager: value,
      };
    });
  };

  const handleDateFilterTypeChange = (
    value,
  ) => {
    setDateFilterType(value);

    setFilterData((prev) => ({
      ...prev,
      dateRangeType: value,
      dateRange: undefined,
    }));
  };

  const handleDateRangeChange = (
    dateStrings,
  ) => {
    setFilterData((prev) => ({
      ...prev,
      dateRange:
        dateStrings?.[0] && dateStrings?.[1]
          ? `${dateStrings[0]}|${dateStrings[1]}`
          : undefined,
    }));
  };

  let selectedRoleValue;

  if (selectedRoleType === "Client Partner") {
    selectedRoleValue = filterData.clientPartner;
  } else if (selectedRoleType === "Delivery Manager") {
    selectedRoleValue = filterData.deliveryManager;
  } else if (selectedRoleType === "Project Manager") {
    selectedRoleValue = filterData.projectManager;
  }

  const discardUnappliedChanges = () => {
    const applied = appliedFilterDataRef.current;

    setFilterData((prev) => ({
      ...applied,
      // Search input should never be lost while closing the popup.
      search: prev.search,
    }));

    if (applied.clientPartner) {
      setSelectedRoleType("Client Partner");
    } else if (applied.deliveryManager) {
      setSelectedRoleType("Delivery Manager");
    } else if (applied.projectManager) {
      setSelectedRoleType("Project Manager");
    } else {
      setSelectedRoleType("");
    }

    setDateFilterType(applied.dateRangeType);
  };

  /*
   * Reset ONLY filter values.
   * Search is preserved and remains applied.
   */
  const clearFilters = () => {
    const currentSearch = filterData.search;
    const trimmedSearch = currentSearch.trim();

    const effectiveSearch =
      trimmedSearch.length < 3 ? "" : trimmedSearch;

    const cleared = {
      search: effectiveSearch,
      statusFilter: undefined,
      deliveryManager: undefined,
      clientPartner: undefined,
      projectManager: undefined,
      dateRangeType: undefined,
      dateRange: undefined,
      contractCategory: undefined,
      businessArea: undefined,
      ragStatus: undefined,
    };

    lastAutoSearchValueRef.current = effectiveSearch;

    setSelectedRoleType("");
    setDateFilterType(undefined);

    // Preserve the exact text in the search input.
    setFilterData({
      ...cleared,
      search: currentSearch,
    });

    setAppliedFilterData(cleared);
    onFilterChange?.(cleared);

    setFilterOpen(false);
  };

  const normalizebusinessAreas = (
    businessArea,
  ) => {
    if (Array.isArray(businessArea)) {
      return businessArea;
    }

    if (businessArea) {
      return [businessArea];
    }

    return [];
  };

  const activeFilterCount = [
    appliedFilterData.statusFilter,
    appliedFilterData.clientPartner,
    appliedFilterData.deliveryManager,
    appliedFilterData.projectManager,
    appliedFilterData.dateRange,
    appliedFilterData.contractCategory,
    Array.isArray(appliedFilterData.businessArea)
      ? appliedFilterData.businessArea.length > 0
      : appliedFilterData.businessArea,
    appliedFilterData.ragStatus,
  ].filter(Boolean).length;

  const selectedbusinessAreas =
    normalizebusinessAreas(filterData.businessArea);

  const trimmedSearchLength =
    filterData.search.trim().length;

  const showSearchError =
    trimmedSearchLength > 0 && trimmedSearchLength < 3;

  const filterContent = (
    <div
      className="project-filter-popup"
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        color: "#334155",
      }}
    >
      {/* Scoped styles - kept inside this component */}
      <style>{`
        .project-filter-popup .ant-select-single,
        .project-filter-popup .ant-picker {
          height: ${FILTER_CONTROL_HEIGHT}px !important;
        }

        .project-filter-popup .ant-select-single .ant-select-selector {
          height: ${FILTER_CONTROL_HEIGHT}px !important;
          display: flex !important;
          align-items: center !important;
        }

        .project-filter-popup .ant-select-single .ant-select-selection-item,
        .project-filter-popup .ant-select-single .ant-select-selection-placeholder {
          line-height: ${FILTER_CONTROL_HEIGHT - 2}px !important;
          font-size: ${FILTER_TEXT_SIZE}px !important;
        }

        .project-filter-popup .ant-select-multiple .ant-select-selector {
          min-height: ${FILTER_CONTROL_HEIGHT}px !important;
          display: flex !important;
          align-items: center !important;
        }

        .project-filter-popup .ant-select-selection-placeholder,
        .project-filter-popup .ant-select-selector .ant-select-selection-placeholder,
        .project-filter-popup .ant-select-selection-item,
        .project-filter-popup .ant-select-selection-search-input,
        .project-filter-popup .ant-picker-input > input {
          font-size: ${FILTER_TEXT_SIZE}px !important;
        }

        .project-filter-popup .ant-select-multiple .ant-select-selection-item {
          font-size: ${FILTER_TEXT_SIZE}px !important;
          line-height: 18px !important;
        }

        .project-filter-popup .ant-picker {
          display: flex !important;
          align-items: center !important;
        }

        .project-filter-popup .ant-radio-wrapper {
          margin-inline-end: 0 !important;
          font-size: ${FILTER_TEXT_SIZE}px !important;
          line-height: 18px !important;
          display: inline-flex !important;
          align-items: center !important;
        }

        /* Smaller and consistent radio circles everywhere */
        .project-filter-popup .ant-radio {
          transform: scale(0.82);
          margin-inline-end: 1px !important;
        }

        .project-filter-popup .ant-radio-wrapper > span:last-child {
          padding-inline-start: 3px !important;
        }

        /*
         * Select / DatePicker dropdown popups render in a portal
         * (appended to document.body), so they are NOT a descendant of
         * .project-filter-popup. We target them via the dedicated
         * popupClassName set on every Select / RangePicker instead.
         */
        .${FILTER_DROPDOWN_CLASS} .ant-select-item,
        .${FILTER_DROPDOWN_CLASS} .ant-select-item-option-content,
        .${FILTER_DROPDOWN_CLASS} .ant-select-item-option {
          font-size: ${FILTER_TEXT_SIZE}px !important;
          line-height: 18px !important;
        }

        .${FILTER_DROPDOWN_CLASS} .ant-empty-description {
          font-size: ${FILTER_TEXT_SIZE}px !important;
        }

        .${FILTER_DROPDOWN_CLASS}.ant-picker-dropdown .ant-picker-header,
        .${FILTER_DROPDOWN_CLASS}.ant-picker-dropdown .ant-picker-header-view,
        .${FILTER_DROPDOWN_CLASS}.ant-picker-dropdown .ant-picker-header button,
        .${FILTER_DROPDOWN_CLASS}.ant-picker-dropdown .ant-picker-cell,
        .${FILTER_DROPDOWN_CLASS}.ant-picker-dropdown .ant-picker-content,
        .${FILTER_DROPDOWN_CLASS}.ant-picker-dropdown .ant-picker-decade-panel,
        .${FILTER_DROPDOWN_CLASS}.ant-picker-dropdown .ant-picker-year-panel,
        .${FILTER_DROPDOWN_CLASS}.ant-picker-dropdown .ant-picker-month-panel {
          font-size: ${FILTER_TEXT_SIZE}px !important;
        }
      `}</style>

      {/* ================= PROJECT STATUS ================= */}
      <div
        style={{
          padding: "7px 0 8px",
          borderBottom: "1px solid #edf0f3",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          <div
            style={{
              ...FILTER_LABEL_STYLE,
              whiteSpace: "nowrap",
            }}
          >
            Project Status
          </div>

          <Radio.Group
            value={filterData.statusFilter ?? ""}
            onChange={(e) =>
              setFilterData((prev) => ({
                ...prev,
                statusFilter:
                  e.target.value || undefined,
              }))
            }
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flex: 1,
              minWidth: 0,
            }}
          >
            <Radio value="" style={FILTER_RADIO_STYLE}>
              All
            </Radio>

            <Radio
              value="true"
              style={FILTER_RADIO_STYLE}
            >
              Active
            </Radio>

            <Radio
              value="false"
              style={FILTER_RADIO_STYLE}
            >
              Inactive
            </Radio>
          </Radio.Group>
        </div>
      </div>

      {/* ================= BUSINESS AREA ================= */}
      <div
        style={{
          padding: "8px 0",
          borderBottom: "1px solid #edf0f3",
        }}
      >
        <div
          style={{
            ...FILTER_LABEL_STYLE,
            marginBottom: 5,
          }}
        >
          Business Area
        </div>

        <Select
          size="middle"
          placeholder="Select business area"
          mode="multiple"
          value={selectedbusinessAreas}
          onChange={(value) =>
            setFilterData((prev) => ({
              ...prev,
              businessArea: value.length
                ? value
                : undefined,
            }))
          }
          allowClear
          loading={loading}
          maxTagCount="responsive"
          style={FILTER_CONTROL_STYLE}
          popupClassName={FILTER_DROPDOWN_CLASS}
          options={businessDomainOptions}
        />
      </div>

      {/* ================= CONTRACT + RAG ================= */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 10,
          padding: "8px 0",
          borderBottom: "1px solid #edf0f3",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              ...FILTER_LABEL_STYLE,
              marginBottom: 5,
            }}
          >
            Contract Category
          </div>

          <Select
            size="middle"
            placeholder="Select category"
            value={filterData.contractCategory}
            onChange={(value) =>
              setFilterData((prev) => ({
                ...prev,
                contractCategory: value,
              }))
            }
            allowClear
            style={FILTER_CONTROL_STYLE}
            popupClassName={FILTER_DROPDOWN_CLASS}
            options={[
              {
                value: "Fixed Bid/Price-Milestone",
                label: "Fixed Bid/Price-Milestone",
              },
              {
                value: "T & M",
                label: "T & M",
              },
              {
                value: "Managed Capacity",
                label: "Managed Capacity",
              },
            ]}
          />
        </div>

        <div style={{ minWidth: 0 }}>
          <div
            style={{
              ...FILTER_LABEL_STYLE,
              marginBottom: 5,
            }}
          >
            RAG Status
          </div>

          <Select
            size="middle"
            placeholder="Select RAG status"
            value={filterData.ragStatus ?? undefined}
            onChange={(value) =>
              setFilterData((prev) => ({
                ...prev,
                ragStatus: value,
              }))
            }
            allowClear
            style={FILTER_CONTROL_STYLE}
            popupClassName={FILTER_DROPDOWN_CLASS}
            options={[
              {
                value: 2,
                label: "At Risk",
              },
              {
                value: 3,
                label: "Needs Attention",
              },
              {
                value: 4,
                label: "On Track",
              },
            ]}
          />
        </div>
      </div>

      {/* ================= ROLES ================= */}
      {!isProjectManagerUser && (
        <div
          style={{
            padding: "8px 0",
            borderBottom: "1px solid #edf0f3",
          }}
        >
          <div
            style={{
              ...FILTER_LABEL_STYLE,
              marginBottom: 6,
            }}
          >
            Roles
          </div>

          <Radio.Group
            value={selectedRoleType}
            onChange={(e) =>
              handleRoleTypeChange(e.target.value)
            }
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              marginBottom: 7,
            }}
          >
            {availableRoles.map((roleType) => (
              <Radio
                key={roleType}
                value={roleType}
                style={FILTER_RADIO_STYLE}
              >
                {roleType}
              </Radio>
            ))}

            <Radio
              value=""
              style={FILTER_RADIO_STYLE}
            >
              None
            </Radio>
          </Radio.Group>

          <Select
            size="middle"
            placeholder="Select based on role"
            value={selectedRoleValue}
            onChange={handleRoleValueChange}
            allowClear
            disabled={!selectedRoleType}
            loading={managerOptionsLoading}
            style={FILTER_CONTROL_STYLE}
            popupClassName={FILTER_DROPDOWN_CLASS}
            options={availableRoleOptions.map((role) => ({
              value: role.name,
              label: role.name,
            }))}
          />
        </div>
      )}

      {/* ================= COMPLETION PERIOD ================= */}
      <div
        style={{
          padding: "8px 0",
          borderBottom: "1px solid #edf0f3",
        }}
      >
        <div
          style={{
            ...FILTER_LABEL_STYLE,
            marginBottom: 5,
          }}
        >
          Project Completion Period By
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Select
            size="middle"
            value={dateFilterType}
            placeholder="Month / Year"
            allowClear
            style={{
              height: FILTER_CONTROL_HEIGHT,
              width:
                dateFilterType === "MONTH" ||
                  dateFilterType === "YEAR"
                  ? 125
                  : "100%",
              flexShrink: 0,
              fontSize: FILTER_TEXT_SIZE,
            }}
            popupClassName={FILTER_DROPDOWN_CLASS}
            onChange={handleDateFilterTypeChange}
            options={[
              {
                value: "MONTH",
                label: "Month",
              },
              {
                value: "YEAR",
                label: "Year",
              },
            ]}
          />

          {dateFilterType === "MONTH" && (
            <DatePicker.RangePicker
              key="MONTH"
              size="middle"
              picker="month"
              style={{
                height: FILTER_CONTROL_HEIGHT,
                flex: 1,
                minWidth: 0,
              }}
              format="MMM YYYY"
              allowClear
              placeholder={["From", "To"]}
              popupClassName={FILTER_DROPDOWN_CLASS}
              onChange={(_, dateStrings) =>
                handleDateRangeChange(
                  dateStrings,
                )
              }
            />
          )}

          {dateFilterType === "YEAR" && (
            <DatePicker.RangePicker
              key="YEAR"
              size="middle"
              picker="year"
              style={{
                height: FILTER_CONTROL_HEIGHT,
                flex: 1,
                minWidth: 0,
              }}
              format="YYYY"
              allowClear
              placeholder={["From", "To"]}
              popupClassName={FILTER_DROPDOWN_CLASS}
              disabledDate={(current) =>
                current.year() < 2000 ||
                current.year() > 2100
              }
              onChange={(_, dateStrings) =>
                handleDateRangeChange(
                  dateStrings,
                )
              }
            />
          )}
        </div>
      </div>

      {/* ================= FOOTER ================= */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingTop: 10,
        }}
      >
        <Button
          type="link"
          size="small"
          onClick={clearFilters}
          style={{
            padding: 0,
            height: 28,
            fontSize: FILTER_TEXT_SIZE,
            fontWeight: 500,
            color: "#2563eb",
          }}
        >
          ↻ Reset Filters
        </Button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Button
            size="middle"
            onClick={() => {
              discardUnappliedChanges();
              setFilterOpen(false);
            }}
            style={{
              minWidth: 86,
              borderRadius: 8,
              fontSize: FILTER_TEXT_SIZE,
            }}
          >
            Cancel
          </Button>

          <Button
            type="primary"
            size="middle"
            onClick={() => {
              /*
               * Apply draft filters.
               * Search remains whatever is currently in the search box.
               */
              const trimmedSearch =
                filterData.search.trim();

              const effectiveSearch =
                trimmedSearch.length < 3
                  ? ""
                  : trimmedSearch;

              const filtersToApply = {
                ...filterData,
                search: effectiveSearch,
              };

              lastAutoSearchValueRef.current =
                effectiveSearch;

              setAppliedFilterData(filtersToApply);
              onFilterChange?.(filtersToApply);
              setFilterOpen(false);
            }}
            style={{
              minWidth: 116,
              borderRadius: 8,
              fontSize: FILTER_TEXT_SIZE,
              fontWeight: 600,
            }}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        paddingBottom: 12,
      }}
    >
      {/* ================= SEARCH ================= */}
      <div style={{ flex: "1 1 240px" }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search by Project ID, SOW ID, Customer Name, Project Name or Technology..."
          value={filterData.search}
          error={showSearchError}
          inputProps={{
            maxLength: SEARCH_MAX_LENGTH,
          }}
          onChange={(e) =>
            setFilterData((prev) => ({
              ...prev,
              search: e.target.value,
            }))
          }
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      fontSize: 17,
                      color: "#64748b",
                    }}
                  />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              height: 40,
              fontSize: 13,
              borderRadius: "4px",
            },
          }}
        />

        <div
          style={{
            minHeight: 18,
            marginTop: 4,
          }}
        >
          {showSearchError && (
            <div
              style={{
                color: "#ff0000",
                fontSize: 12,
              }}
            >
              Minimum 3 characters required to search.
            </div>
          )}
        </div>
      </div>

      {/* ================= FILTER POPOVER ================= */}
      <Popover
        content={filterContent}
        trigger="click"
        open={filterOpen}
        onOpenChange={(open) => {
          if (!open && filterOpen) {

            discardUnappliedChanges();
          }

          setFilterOpen(open);
        }}
        placement="bottomRight"
        autoAdjustOverflow={{
          adjustX: 1,
          adjustY: 1,
        }}
        align={{
          overflow: {
            adjustX: 1,
            adjustY: 1,
          },
        }}
        arrow={false}
        overlayStyle={{
          borderRadius: 10,
          maxWidth: "calc(100vw - 24px)",
        }}
        styles={{
          container: {
            width: FILTER_POPUP_WIDTH,
            maxWidth: "calc(100vw - 24px)",
            padding: "14px 16px",
            borderRadius: 12,
            boxShadow:
              "0 10px 30px rgba(15, 23, 42, 0.14)",
          },
        }}
      >
        <Badge
          count={activeFilterCount}
          size="small"
          offset={[-4, 4]}
        >
          <Button
            size="large"
            icon={
              <img
                src={filterIcon}
                alt="Filter"
                style={{
                  width: 16,
                  height: 16,
                }}
              />
            }
            style={{
              height: 40,
              borderRadius: "4px",
              borderColor:
                activeFilterCount > 0
                  ? "#2563EB"
                  : undefined,
              color:
                activeFilterCount > 0
                  ? "#2563EB"
                  : undefined,
              fontWeight:
                activeFilterCount > 0
                  ? 600
                  : undefined,
              whiteSpace: "nowrap",
            }}
          >
            Filters
          </Button>
        </Badge>
      </Popover>
    </div>
  );
}